import { useCallback, useEffect, useState } from "react";
import { Loader2, MapPin, MessageCircle, PackageX, Phone, RefreshCw, RotateCcw, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";

const FILTERS = [{ k: "", l: "All" }, { k: "NDR", l: "NDR" }, { k: "RTO", l: "RTO" }];
const ACTION_META = {
  REATTEMPT: { label: "Re-attempt", icon: RotateCcw },
  RTO: { label: "Request RTO", icon: PackageX },
  CHANGE_ADDRESS: { label: "Change Address", icon: MapPin },
  CHANGE_PHONE: { label: "Change Phone", icon: Phone },
};

export default function NdrPanel({ dark = false }) {
  const [data, setData] = useState({ items: [], counts: { NDR: 0, RTO: 0 } });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [filter, setFilter] = useState("");
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ remarks: "", address: "", address2: "", city: "", state: "", pincode: "", phone: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (refresh = "") => {
    try {
      const res = await api.get(`/ndr/shipments?state=${filter}${refresh ? "&refresh=1" : ""}`);
      setData(res.data);
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); setSyncing(false); }
  }, [filter]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const sync = () => { setSyncing(true); load("1"); };

  const openModal = (row, action) => {
    setForm({ remarks: "", address: "", address2: "", city: row.receiver_city || "", state: "", pincode: row.receiver_pincode || "", phone: row.receiver_phone || "" });
    setModal({ row, action });
  };

  const submit = async () => {
    setBusy(true);
    try {
      const res = await api.post("/ndr/action", { shipment_id: modal.row.id, action: modal.action, ...form });
      if (res.data.provider_accepted) toast.success("Submitted to courier network");
      else toast.success(`Action recorded. Courier API: ${res.data.provider_message || "will sync when endpoint is enabled"}`);
      setModal(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const wa = (row) => {
    const digits = String(row.receiver_phone || "").replace(/\D/g, "");
    const num = digits.length === 10 ? `91${digits}` : digits;
    const text = encodeURIComponent(`Namaste ${row.receiver_name || ""}, your INDIAFAST shipment ${row.awb} could not be delivered (${row.ndr_reason || "delivery issue"}). Please confirm availability or updated address so we can deliver it. — INDIAFAST Support`);
    return `https://wa.me/${num}?text=${text}`;
  };

  const t = dark
    ? { card: "rounded-xl border border-adminborder bg-admincard", th: "text-slate-500", td: "text-slate-300", input: "rounded-lg border border-adminborder bg-adminbg px-3 py-2 text-sm text-slate-200 outline-none focus:border-saffron", sub: "text-slate-500" }
    : { card: "rounded-xl border border-slate-200 bg-white shadow-sm", th: "text-slate-400", td: "text-slate-600", input: "rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-navy outline-none focus:border-saffron", sub: "text-slate-400" };

  return (
    <div data-testid="ndr-panel">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className={`font-heading text-2xl font-bold ${dark ? "text-white" : "text-navy"}`}>NDR / RTO Management</h1>
          <p className={`mt-1 text-sm ${t.sub}`}>Delivery exceptions auto-detected from courier tracking. Act before the shipment is returned.</p>
        </div>
        <button onClick={sync} disabled={syncing} data-testid="ndr-refresh-btn"
                className="flex items-center gap-2 rounded-lg bg-saffron px-4 py-2.5 text-sm font-bold text-white shadow-lg shadow-saffron/30 hover:bg-saffron-hover disabled:opacity-60">
          {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Sync from Courier
        </button>
      </div>

      <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="ndr-filters">
        {FILTERS.map((f) => (
          <button key={f.k} onClick={() => setFilter(f.k)} data-testid={`ndr-filter-${f.k || "all"}`}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                    filter === f.k ? "bg-saffron text-white" : dark ? "bg-admincard text-slate-400 hover:text-white" : "bg-white text-slate-500 hover:text-navy border border-slate-200"}`}>
            {f.l}{f.k ? ` (${data.counts[f.k] || 0})` : ` (${(data.counts.NDR || 0) + (data.counts.RTO || 0)})`}
          </button>
        ))}
      </div>

      <div className={`${t.card} overflow-x-auto`}>
        {loading ? (
          <div className="flex items-center justify-center gap-2 p-12 text-slate-400" data-testid="ndr-loading"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>
        ) : data.items.length === 0 ? (
          <div className="p-12 text-center" data-testid="ndr-empty">
            <PackageX className={`mx-auto h-10 w-10 ${t.sub}`} />
            <p className={`mt-3 text-sm font-semibold ${dark ? "text-slate-300" : "text-navy"}`}>No NDR/RTO shipments right now</p>
            <p className={`mt-1 text-xs ${t.sub}`}>Click "Sync from Courier" to pull the latest delivery-exception statuses.</p>
          </div>
        ) : (
          <table className="w-full min-w-[900px] text-sm" data-testid="ndr-table">
            <thead>
              <tr className={`${dark ? "border-b border-adminborder" : "border-b border-slate-200"} text-left text-xs uppercase tracking-wider`}>
                {["AWB / Order", "Receiver", "Status", "Reason", "Last Action", "Actions"].map((h) => (
                  <th key={h} className={`px-5 py-3 font-semibold ${t.th}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.items.map((row) => (
                <tr key={row.id} className={dark ? "border-b border-adminborder/60" : "border-b border-slate-100"} data-testid={`ndr-row-${row.awb}`}>
                  <td className="px-5 py-3.5">
                    <p className="font-mono text-xs font-bold text-saffron">{row.awb}</p>
                    <p className={`text-[11px] ${t.sub}`}>{row.order_id} · {row.courier_name}</p>
                  </td>
                  <td className={`px-5 py-3.5 ${t.td}`}>
                    <p className="font-semibold">{row.receiver_name}</p>
                    <p className={`text-xs ${t.sub}`}>{row.receiver_phone} · {row.receiver_city}</p>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${
                      row.ndr_state === "RTO" ? "bg-red-500/15 text-red-400" : "bg-amber-500/15 text-amber-400"}`}
                      data-testid={`ndr-state-${row.awb}`}>{row.ndr_state}</span>
                  </td>
                  <td className={`max-w-[220px] px-5 py-3.5 text-xs ${t.td}`}>{row.ndr_reason}</td>
                  <td className={`px-5 py-3.5 text-xs ${t.sub}`}>
                    {row.ndr_actions?.length ? (
                      <span data-testid={`ndr-history-${row.awb}`}>
                        {ACTION_META[row.ndr_actions[row.ndr_actions.length - 1].action]?.label || row.ndr_actions[row.ndr_actions.length - 1].action}
                        {" · "}{(row.ndr_actions[row.ndr_actions.length - 1].at || "").slice(0, 10)}
                        {row.ndr_actions.length > 1 && ` (+${row.ndr_actions.length - 1})`}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex flex-wrap gap-1.5">
                      {row.receiver_phone && (
                        <a href={wa(row)} target="_blank" rel="noreferrer" data-testid={`ndr-whatsapp-${row.id}`}
                           className="grid h-8 w-8 place-items-center rounded-lg bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25" title="WhatsApp customer">
                          <MessageCircle className="h-4 w-4" />
                        </a>
                      )}
                      {Object.keys(ACTION_META).map((a) => {
                        const M = ACTION_META[a];
                        return (
                          <button key={a} onClick={() => openModal(row, a)} data-testid={`ndr-act-${a.toLowerCase()}-${row.id}`}
                                  className={`flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-bold ${
                                    a === "RTO" ? "bg-red-500/15 text-red-400 hover:bg-red-500/25" : dark ? "bg-white/5 text-slate-300 hover:bg-white/10" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                            <M.icon className="h-3.5 w-3.5" /> {M.label}
                          </button>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" data-testid="ndr-action-modal">
          <div className={`w-full max-w-md rounded-2xl p-6 ${dark ? "border border-adminborder bg-admincard" : "bg-white shadow-2xl"}`}>
            <div className="flex items-center justify-between">
              <h3 className={`font-heading text-lg font-bold ${dark ? "text-white" : "text-navy"}`}>
                {ACTION_META[modal.action].label} — <span className="font-mono text-sm text-saffron">{modal.row.awb}</span>
              </h3>
              <button onClick={() => setModal(null)} data-testid="ndr-modal-close" className={t.sub}><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-4 space-y-3">
              {modal.action === "CHANGE_ADDRESS" && (
                <>
                  <input className={`${t.input} w-full`} data-testid="ndr-modal-address" placeholder="New address line 1 *" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                  <input className={`${t.input} w-full`} data-testid="ndr-modal-address2" placeholder="Address line 2" value={form.address2} onChange={(e) => setForm({ ...form, address2: e.target.value })} />
                  <div className="grid grid-cols-3 gap-2">
                    <input className={t.input} data-testid="ndr-modal-city" placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                    <input className={t.input} data-testid="ndr-modal-state" placeholder="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
                    <input className={t.input} data-testid="ndr-modal-pincode" placeholder="Pincode *" value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} />
                  </div>
                </>
              )}
              {modal.action === "CHANGE_PHONE" && (
                <input className={`${t.input} w-full`} data-testid="ndr-modal-phone" placeholder="New 10-digit phone *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              )}
              {modal.action === "RTO" && (
                <p className="rounded-lg bg-red-500/10 p-3 text-xs font-semibold text-red-400" data-testid="ndr-rto-warning">
                  This requests return-to-origin with the courier. Use only after re-attempts are exhausted.
                </p>
              )}
              <textarea rows={3} className={`${t.input} w-full`} data-testid="ndr-modal-remarks" placeholder="Remarks for the courier / internal note…" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setModal(null)} className={`rounded-lg px-4 py-2 text-sm font-semibold ${dark ? "text-slate-400 hover:bg-white/5" : "text-slate-500 hover:bg-slate-100"}`}>Cancel</button>
              <button onClick={submit} disabled={busy} data-testid="ndr-modal-submit"
                      className="flex items-center gap-2 rounded-lg bg-saffron px-5 py-2 text-sm font-bold text-white hover:bg-saffron-hover disabled:opacity-60">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
